import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline"
import { Progress } from "antd"
import { Button, P2 } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useProcessMonitorStore } from "stores/process-monitor"
import ProcessMonitorIcon from "./ProcessMonitorIcon"
import { ProcessLoaderIcon } from "./ProcessStatusIcons"
import {
  getDisplayedProgress,
  hasReportedProgress,
  isActiveTransaction,
  shouldDisplayProgressPercentage,
} from "./types"

const ProcessProgressModal = () => {
  const focusedProcessKey = useProcessMonitorStore(
    (state) => state.focusedProcessKey
  )
  const process = useProcessMonitorStore((state) =>
    focusedProcessKey ? state.processes[focusedProcessKey] : undefined
  )
  const setFocusedProcess = useProcessMonitorStore(
    (state) => state.setFocusedProcess
  )

  if (!process) return null

  const status = process.status.toLowerCase()
  const progress = getDisplayedProgress(process)
  const isActive = isActiveTransaction(status)
  const isIndeterminate = isActive && !hasReportedProgress(process)
  const isFailed =
    status === "failed" ||
    status === "stopped" ||
    status === "completed_with_errors"
  const failureLabel =
    status === "completed_with_errors"
      ? "Completed with errors"
      : status === "stopped"
      ? "Stopped"
      : "Failed"
  const StatusIcon = isActive
    ? ProcessLoaderIcon
    : isFailed
    ? ExclamationCircleIcon
    : CheckCircleIcon
  return (
    <CustomModal
      open
      width={500}
      title={process.title || "Process progress"}
      closable
      maskClosable={false}
      destroyOnClose
      wrapClassName="[&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-[12px] [&_.ant-modal-content]:!p-0"
      bodyClassName="mt-4 px-4 md:px-6"
      footerClassName="w-full border-t px-4 py-4 md:px-6"
      onCancel={() => setFocusedProcess(undefined)}
      footer={
        <div className="w-full">
          {isActive && (
            <P2 className="mb-4 text-neutral300">
              You can close this and track progress from the{" "}
              <span className="inline-flex items-center gap-1 font-semibold text-primary500">
                <ProcessMonitorIcon className="h-3 w-3" />
                Process Monitor
              </span>{" "}
              in the top bar.
            </P2>
          )}
          <Button
            color="primary"
            className="w-full"
            onClick={() => setFocusedProcess(undefined)}
          >
            {isActive ? "Continue in background" : "Close"}
          </Button>
        </div>
      }
    >
      <div className="font-avenir">
        <P2 className="leading-6 text-neutral300">
          {isActive
            ? "We're processing this task in the background. We'll notify you as soon as it's ready."
            : "This task has finished. You can review its final status from the Process Monitor."}
        </P2>

        <div className="-mx-4 mt-4 border-y border-neutral30 px-4 py-4 md:-mx-6 md:px-6">
          <div className="flex items-center gap-2">
            <StatusIcon
              className={`shrink-0 ${
                isActive
                  ? "h-[18px] w-[18px] animate-spin text-[#0F172B]"
                  : isFailed
                  ? "h-5 w-5 text-danger500"
                  : "h-5 w-5 text-success500"
              }`}
            />

            <div className="flex min-w-0 flex-1 items-baseline gap-2 text-sm font-semibold text-neutral700">
              <span className="min-w-0 truncate">{process.message}</span>
            </div>
            <span
              className={`shrink-0 text-xs font-semibold ${
                isFailed ? "text-danger500" : "text-neutral500"
              }`}
            >
              {isIndeterminate
                ? null
                : shouldDisplayProgressPercentage(status)
                ? `${progress}%`
                : failureLabel}
            </span>
          </div>

          {isIndeterminate ? (
            <div
              className="process-monitor-indeterminate mt-2"
              role="progressbar"
              aria-label={`${process.title || process.type} is in progress`}
            >
              <span aria-hidden="true" />
            </div>
          ) : (
            <Progress
              className="mt-2"
              percent={progress}
              showInfo={false}
              size="small"
              status={isActive ? "active" : "normal"}
              strokeColor={isFailed ? "#f41418" : "rgb(var(--color-primary))"}
              trailColor="#edf0f2"
            />
          )}
        </div>
      </div>
    </CustomModal>
  )
}

export default ProcessProgressModal
